<template>
  <template v-if="!treeData || !treeData.length">
    <el-alert
      type="info"
      show-icon
      :closable="false"
      class="access-empty-alert"
    >
      <template #title>
        <span>Пока нет объектов для назначения доступа.</span>
        <el-link
          type="primary"
          style="margin-left: 6px;"
          @click="$router.push('/branches')"
        >
          Перейти к настройке локаций
        </el-link>
      </template>
    </el-alert>
  </template>
  <div class="access-tree">
    <template v-for="city in treeData" :key="city.id">
      <div class="node city-group">
        <div class="node-header">
          <span @click="toggleExpand(`city_${city.id}`)" class="toggle-icon">
            {{ expandedState[`city_${city.id}`] ? '▾' : '▸' }}
          </span>
          <font-awesome-icon :icon="['fas', 'city']" size="sm" class="node-icon" />
          <span>{{ city.name }}</span>
        </div>

        <div v-show="expandedState[`city_${city.id}`]" v-if="city.children?.length" class="node-children">
          <template v-for="branch in city.children" :key="branch.id">
            <div class="node">
              <div class="node-header">
                <span @click="toggleExpand(`branch_${branch.id}`)" class="toggle-icon">
                  {{ expandedState[`branch_${branch.id}`] ? '▾' : '▸' }}
                </span>
                <font-awesome-icon :icon="['fas', 'building']" size="sm" class="node-icon" />
                <input
                  type="checkbox"
                  :checked="isChecked(`branch_${branch.id}`)"
                  @change="toggleCheck(branch, 'branch')"
                  :disabled="isAnyRoomSelected(branch)"
                  :ref="`branch_checkbox_${branch.id}`"
                />
                <span @click="toggleCheck(branch, 'branch')" class="clickable-label">
                  {{ branch.name }}
                  <small v-if="isChecked(`branch_${branch.id}`)" class="hint-text">— Работник офиса (не отображается в календаре)</small>
                </span>
              </div>

              <div v-show="expandedState[`branch_${branch.id}`]" v-if="branch.children?.length" class="node-children">
                <div
                  v-for="room in branch.children"
                  :key="room.id"
                  class="node"
                >
                  <div class="node-header">
                    <font-awesome-icon :icon="['fas', 'door-closed']" size="sm" class="node-icon" />
                    <input
                      type="checkbox"
                      :checked="isChecked(`room_${room.id}`)"
                      @change="toggleCheck(room, 'room', branch)"
                      :disabled="isChecked(`branch_${branch.id}`)"
                    />
                    <span @click="toggleCheck(room, 'room', branch)" class="clickable-label">
                      {{ room.name }}
                      <small v-if="isChecked(`room_${room.id}`)" class="hint-text">— Работник кабинета (отображается в календаре)</small>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>
    </template>
  </div>
</template>

<script>

export default {
  name: 'AccessTree',
  props: {
    selectedAccess: { type: Array, default: () => [] },
    treeData: { type: Array, required: true },
    readonly: { type: Boolean, default: false }
  },
  data() {
    return {
      checked: new Set(this.selectedAccess.map(loc => {
        if (loc.room_id) return `room_${loc.room_id}`
        if (loc.branch_id) return `branch_${loc.branch_id}`
        return null
      }).filter(Boolean)),
      expandedState: {}
    }
  },
  created() {
    this.treeData.forEach(city => {
      this.expandedState[`city_${city.id}`] = true
      city.children?.forEach(branch => {
        this.expandedState[`branch_${branch.id}`] = true
      })
    })
  },
  methods: {
    isChecked(key) {
      return this.checked.has(key)
    },
    isAnyRoomSelected(branch) {
      return branch.children?.some(room => this.checked.has(`room_${room.id}`)) ?? false
    },
    findNodeByTypeAndId(type, id) {
      for (const city of this.treeData) {
        if (type === 'branch') {
          const found = city.children?.find(b => b.id === id)
          if (found) return { ...found, city_id: city.id }
        }
        if (type === 'room') {
          for (const branch of city.children || []) {
            const found = branch.children?.find(r => r.id === id)
            if (found) return { ...found, city_id: city.id, branch_id: branch.id }
          }
        }
      }
      return null
    },
    toggleCheck(node, type, branch = null) {
      if (type === 'branch') {
        const branch = node
        const branchKey = `branch_${branch.id}`
        if (this.checked.has(branchKey)) {
          this.checked.delete(branchKey)
        } else {
          this.checked.add(branchKey)
          // When branch is checked, disable all its child rooms by removing them from checked
          branch.children?.forEach(room => {
            this.checked.delete(`room_${room.id}`)
          })
        }
      } else if (type === 'room') {
        const roomKey = `room_${node.id}`
        if (this.checked.has(roomKey)) {
          this.checked.delete(roomKey)
        } else {
          this.checked.add(roomKey)
          // When any room is selected, disable parent branch by removing it from checked
          if (branch) {
            this.checked.delete(`branch_${branch.id}`)
          }
        }
      }

      const locations = Array.from(this.checked).map(k => {
        if (k.startsWith('room_')) {
          const id = +k.split('_')[1]
          const roomNode = this.findNodeByTypeAndId('room', id)
          return { city_id: roomNode?.city_id ?? null, branch_id: roomNode?.branch_id ?? null, room_id: roomNode?.id ?? id }
        }
        if (k.startsWith('branch_')) {
          const id = +k.split('_')[1]
          const branchNode = this.findNodeByTypeAndId('branch', id)
          return { city_id: branchNode?.city_id ?? null, branch_id: branchNode?.id ?? id, room_id: null }
        }
        return undefined
      }).filter(Boolean)

      this.$emit('update:selectedAccess', locations)
    },
    toggleExpand(key) {
      this.expandedState[key] = !this.expandedState[key]
    },
  }
}
</script>

<style scoped>
.access-tree {
  font-size: 14px;
  line-height: 1.6;
}
.city-group {
  margin-bottom: 24px;
}
.node {
  margin-left: 16px;
}
.node-header {
  display: flex;
  align-items: center;
  gap: 8px;
}
.node-children {
  margin-left: 16px;
  margin-top: 12px;
}

.node + .node {
  margin-top: 8px;
}
.toggle-icon {
  cursor: pointer;
  margin-right: 4px;
  font-weight: bold;
  user-select: none;
}
.node-icon {
  margin-right: 4px;
  user-select: none;
}

.hint-text {
  font-size: 12px;
  color: #888;
}

.clickable-label {
  cursor: pointer;
}
</style>
